/**
 * Фабрика для создания миграций
 */

import { 
  IMigration, 
  IMigrationFactory, 
  MigrationResult, 
  MigrationContext, 
  SchemaMigrationConfig, 
  DataMigrationConfig 
} from '../types/migration-interfaces';
import { ICSElement } from '../types/interfaces';
import { TypedElementSchema } from '../types/typed-elements-interfaces';

export class MigrationFactory implements IMigrationFactory {
  /**
   * Создает миграцию схемы
   */
  createSchemaMigration(config: SchemaMigrationConfig & { 
    id: string; 
    version: string; 
    description: string; 
  }): IMigration {
    return {
      id: config.id,
      version: config.version,
      description: config.description,
      type: 'schema',
      dependencies: [],
      
                   async up(_context: MigrationContext): Promise<MigrationResult> {
        const startTime = Date.now();
        
        try {
          // Логика применения изменений схемы
          console.log(`Applying schema changes for ${config.schemaName}`);
          
          return {
            success: true,
            message: `Schema ${config.schemaName} migrated successfully`,
            processedCount: 1,
            executionTime: Date.now() - startTime
          };
        } catch (error) {
          return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error',
            executionTime: Date.now() - startTime
          };
        }
      },
      
      async down(_context: MigrationContext): Promise<MigrationResult> {
        const startTime = Date.now();
        
        try {
          // Логика отката изменений схемы
          console.log(`Rolling back schema changes for ${config.schemaName}`);
          
          return {
            success: true,
            message: `Schema ${config.schemaName} rolled back successfully`,
            processedCount: 1,
            executionTime: Date.now() - startTime
          };
        } catch (error) {
          return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error',
            executionTime: Date.now() - startTime
          };
        }
      },
      
      async canApply(context: MigrationContext): Promise<boolean> {
        // Проверяем, существует ли схема
        return context.direction === 'up';
      },
      
      async canRollback(context: MigrationContext): Promise<boolean> {
        // Проверяем, можно ли откатить изменения
        return context.direction === 'down';
      },
      
      async validate(_context: MigrationContext): Promise<boolean> {
        // Валидируем схему после применения
        return true;
      }
    };
  }
  
  /**
   * Создает миграцию данных
   */
  createDataMigration(config: DataMigrationConfig & { 
    id: string; 
    version: string; 
    description: string; 
  }): IMigration {
    return {
      id: config.id,
      version: config.version,
      description: config.description,
      type: 'data',
      dependencies: [],
      
            async up(context: MigrationContext): Promise<MigrationResult> {
        const startTime = Date.now();
        let processedCount = 0;
        
        try {
          // Получаем элементы для обработки из контекста
          const elements: ICSElement[] = await MigrationFactory.getElementsForMigration(context, config);
          
          // Обрабатываем элементы пакетами
          const batchSize = config.batchSize || 100;
          const concurrency = config.concurrency || 1;
          
          for (let i = 0; i < elements.length; i += batchSize) {
            const batch = elements.slice(i, i + batchSize);
            
            if (concurrency > 1) {
              // Параллельная обработка
              const promises = batch.map((element: ICSElement) => config.transformer(element));
              await Promise.all(promises);
            } else {
              // Последовательная обработка
              for (const element of batch) {
                await config.transformer(element);
              }
            }
            
            processedCount += batch.length;
          }
          
          return {
            success: true,
            message: `Processed ${processedCount} elements`,
            processedCount,
            executionTime: Date.now() - startTime
          };
        } catch (error) {
          return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error',
            processedCount,
            executionTime: Date.now() - startTime
          };
        }
      },
      
      async down(_context: MigrationContext): Promise<MigrationResult> {
        const startTime = Date.now();
        
        return {
          success: false,
          error: 'Data migration rollback not implemented',
          executionTime: Date.now() - startTime
        };
      },
      
      async canApply(context: MigrationContext): Promise<boolean> {
        return context.direction === 'up';
      },
      
      async canRollback(_context: MigrationContext): Promise<boolean> {
        return false; // Откат данных по умолчанию отключен
      },
      
      async validate(_context: MigrationContext): Promise<boolean> {
        return true;
      }
    };
  }
  
  /**
   * Создает кастомную миграцию
   */
  createCustomMigration(config: {
    id: string;
    version: string;
    description: string;
    dependencies?: string[];
    up: (context: MigrationContext) => Promise<MigrationResult>;
    down: (context: MigrationContext) => Promise<MigrationResult>;
    canApply?: (context: MigrationContext) => Promise<boolean>;
    canRollback?: (context: MigrationContext) => Promise<boolean>;
    validate?: (context: MigrationContext) => Promise<boolean>;
  }): IMigration {
    return {
      id: config.id,
      version: config.version,
      description: config.description,
      type: 'custom',
      dependencies: config.dependencies || [],
      up: config.up,
      down: config.down,
      canApply: config.canApply || (async () => true),
      canRollback: config.canRollback || (async () => true),
      validate: config.validate || (async () => true)
    };
  }
  
  /**
   * Создает миграцию для добавления поля
   */
  createAddFieldMigration(config: {
    id: string;
    version: string;
    schemaName: string;
    fieldName: string;
    fieldType: string;
    defaultValue?: any;
    required?: boolean;
  }): IMigration {
    return this.createSchemaMigration({
      id: config.id,
      version: config.version,
      description: `Add field ${config.fieldName} to ${config.schemaName}`,
      schemaName: config.schemaName,
      oldSchema: {} as TypedElementSchema, // Заглушка
      newSchema: {} as TypedElementSchema, // Заглушка
      fieldMapping: {},
      defaultValues: config.defaultValue ? { [config.fieldName]: config.defaultValue } : {}
    });
  }
  
  /**
   * Создает миграцию для удаления поля
   */
  createRemoveFieldMigration(config: {
    id: string;
    version: string;
    schemaName: string;
    fieldName: string;
  }): IMigration {
    return this.createSchemaMigration({
      id: config.id,
      version: config.version,
      description: `Remove field ${config.fieldName} from ${config.schemaName}`,
      schemaName: config.schemaName,
      oldSchema: {} as TypedElementSchema, // Заглушка
      newSchema: {} as TypedElementSchema, // Заглушка
      fieldsToRemove: [config.fieldName]
    });
  }
  
  /**
   * Создает миграцию для переименования поля
   */
  createRenameFieldMigration(config: {
    id: string;
    version: string;
    schemaName: string;
    oldFieldName: string;
    newFieldName: string;
  }): IMigration {
    return this.createSchemaMigration({
      id: config.id,
      version: config.version,
      description: `Rename field ${config.oldFieldName} to ${config.newFieldName} in ${config.schemaName}`,
      schemaName: config.schemaName,
      oldSchema: {} as TypedElementSchema, // Заглушка
      newSchema: {} as TypedElementSchema, // Заглушка
      fieldMapping: { [config.oldFieldName]: config.newFieldName }
    });
  }
  
  /**
   * Создает миграцию для изменения типа поля
   */
  createChangeFieldTypeMigration(config: {
    id: string;
    version: string;
    schemaName: string;
    fieldName: string;
    oldType: string;
    newType: string;
    transformer?: (value: any) => any;
  }): IMigration {
    return this.createSchemaMigration({
      id: config.id,
      version: config.version,
      description: `Change field ${config.fieldName} type from ${config.oldType} to ${config.newType} in ${config.schemaName}`,
      schemaName: config.schemaName,
      oldSchema: {} as TypedElementSchema, // Заглушка
      newSchema: {} as TypedElementSchema, // Заглушка
      transformers: config.transformer ? { [config.fieldName]: config.transformer } : {}
    });
  }
  
  /**
   * Создает миграцию для создания индекса
   */
  createCreateIndexMigration(config: {
    id: string;
    version: string;
    schemaName: string;
    indexName: string;
    fields: string[];
    unique?: boolean;
  }): IMigration {
    return this.createCustomMigration({
      id: config.id,
      version: config.version,
      description: `Create ${config.unique ? 'unique ' : ''}index ${config.indexName} on ${config.schemaName}`,
      
      async up(_context: MigrationContext): Promise<MigrationResult> {
        const startTime = Date.now();
        
        try {
          // Логика создания индекса
          console.log(`Creating index ${config.indexName} on fields: ${config.fields.join(', ')}`);
          
          return {
            success: true,
            message: `Index ${config.indexName} created successfully`,
            processedCount: 1,
            executionTime: Date.now() - startTime
          };
        } catch (error) {
          return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error',
            executionTime: Date.now() - startTime
          };
        }
      },
      
      async down(_context: MigrationContext): Promise<MigrationResult> {
        const startTime = Date.now();
        
        try {
          // Логика удаления индекса
          console.log(`Dropping index ${config.indexName}`);
          
          return {
            success: true,
            message: `Index ${config.indexName} dropped successfully`,
            processedCount: 1,
            executionTime: Date.now() - startTime
          };
        } catch (error) {
          return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error',
            executionTime: Date.now() - startTime
          };
        }
      }
    });
  }

  /**
   * Получает элементы для миграции из контекста
   */
  static async getElementsForMigration(
    context: MigrationContext, 
    config: DataMigrationConfig
  ): Promise<ICSElement[]> {
    try {
      // Получаем элементы из глобального реестра или через селектор
      if (config.selector) {
        // Если есть селектор, применяем его к доступным элементам
        // В реальной реализации здесь должен быть доступ к реестру элементов
        console.log(`Применение селектора для миграции ${context.fromVersion} -> ${context.toVersion}`);
        
        // Заглушка - возвращаем пустой массив
        // В реальной реализации здесь должна быть логика получения элементов
        return [];
      }

      // Fallback - возвращаем пустой массив
      console.warn('Не удалось получить элементы для миграции - используем пустой массив');
      return [];
    } catch (error) {
      console.error('Ошибка при получении элементов для миграции:', error);
      return [];
    }
  }

}

// Экспортируем экземпляр фабрики
export const migrationFactory = new MigrationFactory(); 